import { useRef, useState } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';

import { Grid } from '@mui/material';
import { AddVariants } from './add-variants';
import { VariantsList } from './variants-list';
import { VariantLibraryModal } from 'pages/variants/modals/variant-library-modal';
import { NewVariantModal } from './new-variant-modal';

import { ISelectionFormOptions } from 'widgets/selections/utils/types';
import {
  TVariantLibraryFormOption,
  VariantLibrayForm,
} from 'pages/variants/modals/variant-library-modal/types';
import { useModalState } from 'hooks/useModalState';
import { useGetSelectionVariantTypesQuery } from 'redux-store/api/selection-variant';
import { adaptVariantToLibraryForm, getSelectableVariants } from './utils';
import { NoVariantsModal } from './no-variants-modal';

interface IVariants {
  optionIndex: number;
  isSelectionApproved?: boolean;
}

export const Variants = ({ optionIndex, isSelectionApproved = false }: IVariants) => {
  const form = useFormContext<ISelectionFormOptions>();

  const { fields, remove, append, update } = useFieldArray({
    control: form.control,
    name: `options.${optionIndex}.variants`,
    keyName: 'key',
  });

  const [isLibraryModalOpen, openLibraryModal, closeLibraryModal] = useModalState();
  const [isNoVariantsModalOpen, openNoVariantsModal, closeNoVariantsModal] = useModalState();
  const [isNewVariantModalOpen, openNewVariantModal, closeNewVariantModal] = useModalState();

  const [libraryDefaultValues, setLibraryDefaultValues] = useState<VariantLibrayForm | null>(null);
  const [editLibraryIndex, setEditLibraryIndex] = useState<number | null>(null);

  const imperativeRef = useRef<{
    setOption: (options: TVariantLibraryFormOption[]) => void;
  } | null>(null);

  const currentOption = form.watch(`options.${optionIndex}`);

  const { data: variantTypesResponse } = useGetSelectionVariantTypesQuery();

  const handleAddNewVariant = () => {
    selectableVariants.length ? openLibraryModal() : openNoVariantsModal();
  };

  const handleEditVariant = (index: number) => {
    setEditLibraryIndex(index);

    const currentVariant = currentOption.variants[index];
    setLibraryDefaultValues(adaptVariantToLibraryForm(currentVariant, variantTypesResponse?.items));

    openLibraryModal();
  };

  const handleRemoveVariant = (index: number) => {
    remove(index);
  };

  const handleCloseLibrary = () => {
    setLibraryDefaultValues(null);
    setEditLibraryIndex(null);
    closeLibraryModal();
  };

  const handleSubmitLibrary = (formValues: VariantLibrayForm) => {
    const { name, options } = formValues;

    if (editLibraryIndex !== null) {
      update(editLibraryIndex, { name, options });
    } else {
      append({ name, options });
    }

    handleCloseLibrary();
  };

  const handleSubmitNewVariant = (options: TVariantLibraryFormOption[]) => {
    imperativeRef.current?.setOption(options);
    closeNewVariantModal();
  };

  const selectableVariants = getSelectableVariants(
    libraryDefaultValues?.name,
    currentOption?.variants,
    variantTypesResponse?.items
  );

  return (
    <Grid>
      <AddVariants isDisabled={isSelectionApproved} onAdd={handleAddNewVariant} />

      <VariantsList
        isSelectionApproved={isSelectionApproved}
        variants={fields}
        onEdit={handleEditVariant}
        onDelete={handleRemoveVariant}
      />

      {isNoVariantsModalOpen && <NoVariantsModal open onClose={closeNoVariantsModal} />}

      {isLibraryModalOpen && (
        <VariantLibraryModal
          open
          imperativeRef={imperativeRef}
          variants={selectableVariants}
          defaultValues={libraryDefaultValues ?? { name: '', options: [] }}
          onImportChoices={openNewVariantModal}
          onClose={handleCloseLibrary}
          onSubmit={handleSubmitLibrary}
        />
      )}

      {isNewVariantModalOpen && (
        <NewVariantModal open onSubmit={handleSubmitNewVariant} onClose={closeNewVariantModal} />
      )}
    </Grid>
  );
};
